import React, { Component } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight, BarChart2, Users, Layers, Tag, Zap, TrendingUp, TrendingDown, MessageSquare, Lightbulb } from "lucide-react";

import {
  ResetAnalysisReduxStateAction,
  ResetAnalysisMessageAction,
  FetchIndividualFileAction,
} from "../../../Actions/Analysis";
import BackbuttonSVG from "../../../Assets/svg/BackbuttonSVG.svg";
import SurveyAnalysisSummary from "./SurveyAnalysisSummary";

class DataAnalysisSummary extends Component {
  // Non-analysis columns — used to count survey questions
  static ANALYSIS_KEYS = new Set([
    "embeddings", "Centroids", "processed_text", "Cluster Summary",
    "Cluster Phrase", "Cluster Theme Description", "Cluster",
    "Positive", "Neutral", "Negative", "Satisfied", "Neutral_Satisfaction",
    "Dissatisfied", "Sentiment", "Satisfaction",
    "CustomerID", "GuestID", "RespondentID", "ID",
  ]);

  state = {
    showAnalytics: false,
    isExporting: false,
  };

  topRef = React.createRef();
  contentRef = React.createRef();

  scrollToTop = () => {
    this.topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  static propTypes = {
    csvAnalysisSuccessReport: PropTypes.string,
    csvAnalysisSuccess: PropTypes.bool,
    ResetAnalysisReduxStateAction: PropTypes.func.isRequired,
    analysisFileName: PropTypes.string,
    ResetAnalysisMessageAction: PropTypes.func.isRequired,
    pdfAnalysisMessage: PropTypes.string,
    pdfAnalysisOutput: PropTypes.any,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    FetchIndividualFileAction: PropTypes.func.isRequired,
    isIndividualFileFetched: PropTypes.bool,
    AddedFileID: PropTypes.string,
  };

  componentDidMount() {
    // Clear stale data from the previous file before fetching the new one
    this.props.ResetAnalysisReduxStateAction();
    this.props.ResetAnalysisMessageAction();

    const path = this.props.location.pathname;
    const segments = path.split("/");
    const id = segments[segments.length - 1];
    this.props.FetchIndividualFileAction(id);
  }

  componentDidUpdate(prevProps) {
    // If the URL changed (navigated to a different result), reset and re-fetch
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.props.ResetAnalysisReduxStateAction();
      this.props.ResetAnalysisMessageAction();

      const segments = this.props.location.pathname.split("/");
      const id = segments[segments.length - 1];
      this.props.FetchIndividualFileAction(id);
    }
  }

  handlePath = () => {
    const { pathname } = this.props.location;
    return pathname.includes("/dashboard/pdf-analyzer/result-summary");
  };

  handleBackToHome = () => {
    if (
      this.props.location.pathname.includes(
        "/dashboard/pdf-analyzer/result-summary/"
      )
    ) {
      this.props.navigate("/dashboard/pdf-analyzer");
    } else if (
      this.props.location.pathname.includes("/dashboard/result-summary")
    ) {
      this.props.navigate("/dashboard");
    }

    // this.props.navigate("..");
    this.props.ResetAnalysisReduxStateAction();
    this.props.ResetAnalysisMessageAction();
  };

  handleCompare = () => {
    this.props.navigate("compare");
  };

  surveyMarkdownComponents = {
    h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-5 mb-2 text-gray-900 border-b border-indigo-100 pb-1.5 font-inter" {...props} />,
    h2: ({ node, ...props }) => <h2 className="text-lg font-semibold mt-5 mb-2 text-indigo-700 font-inter" {...props} />,
    h3: ({ node, ...props }) => <h3 className="text-base font-semibold mt-3 mb-1 text-gray-700 font-inter" {...props} />,
    p:  ({ node, ...props }) => <p className="text-base text-gray-700 leading-relaxed mb-3 font-inter" {...props} />,
    strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
    ul: ({ node, ...props }) => <ul className="ml-4 mb-3 space-y-1.5 list-disc" {...props} />,
    ol: ({ node, ...props }) => <ol className="ml-4 mb-3 space-y-1.5 list-decimal" {...props} />,
    li: ({ node, ...props }) => <li className="text-base text-gray-700 leading-relaxed font-inter" {...props} />,
    hr: ({ node, ...props }) => <hr className="my-4 border-gray-200" {...props} />,
  };

  // Helper function to remove HTML tags
  stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  // Function to detect if content is HTML
  isHtmlContent = (content) => {
    if (typeof content !== "string") return false;
    // Check for common HTML tags
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(content);
  };

  // Function to detect if content is Markdown
  isMarkdownContent = (content) => {
    if (typeof content !== "string") return false;
    // Check for common markdown patterns
    const markdownPatterns = [
      /^#{1,6}\s+/m, // Headers
      /\*\*.*?\*\*/, // Bold text
      /\*.*?\*/, // Italic text
      /^-\s+/m, // List items
      /^\d+\.\s+/m, // Numbered lists
      /\[.*?\]\(.*?\)/, // Links
      /^>\s+/m, // Blockquotes
      /```[\s\S]*?```/, // Code blocks
      /`.*?`/, // Inline code
    ];

    return markdownPatterns.some((pattern) => pattern.test(content));
  };

  // Function to get content from analysis output
  getAnalysisContent = (pdfAnalysisOutput) => {
    // Check if the new structure exists (analysis_output.data)
    if (
      pdfAnalysisOutput &&
      pdfAnalysisOutput.analysis_output &&
      pdfAnalysisOutput.analysis_output.data
    ) {
      return pdfAnalysisOutput.analysis_output.data;
    }
    // Fall back to processing the entire object
    return this.processDataToText(pdfAnalysisOutput);
  };

  processDataToText = (data, indentLevel = 0) => {
    let output = "";
    const indent = "    ".repeat(indentLevel);

    if (typeof data === "string") {
      try {
        data = JSON.parse(data.replace(/'/g, '"'));
      } catch (e) {
        output += `${indent}${data}\n`;
        return output;
      }
    }

    if (Array.isArray(data)) {
      // For arrays in the data field, don't show indices
      if (indentLevel === 0 && data.length === 1) {
        return this.processDataToText(data[0], indentLevel);
      }
      data.forEach((item) => {
        output += this.processDataToText(item, indentLevel);
      });
    } else if (typeof data === "object" && data !== null) {
      for (const [key, value] of Object.entries(data)) {
        // Skip metadata fields that we don't want to display
        if (key === "status" || key === "last_edited" || key === "content_type")
          continue;

        // If the key is "content", return the content directly without any formatting or labels
        if (key === "content") {
          if (typeof value === "string") {
            output += value;
          } else {
            output += this.processDataToText(value, indentLevel);
          }
          continue;
        }

        // Special handling for the "data" field at root level
        if (indentLevel === 0 && key === "data") {
          output += this.processDataToText(value, indentLevel);
          continue;
        }

        // For all other fields, format them with labels
        output += `${indent}${key}:\n`;
        output += this.processDataToText(value, indentLevel + 1);
      }
    } else {
      output += `${indent}${data}\n`;
    }

    return output;
  };

  handlePathText = () => {
    const {
      location,
      csvAnalysisSuccessReport,
      pdfAnalysisMessage,
      pdfAnalysisOutput,
    } = this.props;

    if (location.pathname.includes("/dashboard/result-summary")) {
      return csvAnalysisSuccessReport || "No CSV analysis data available.";
    } else if (
      location.pathname.includes("/dashboard/pdf-analyzer/result-summary")
    ) {
      return pdfAnalysisOutput
        ? this.getAnalysisContent(pdfAnalysisOutput)
        : pdfAnalysisMessage || "No PDF analysis data available.";
    }

    return "No data available.";
  };

  // Detect survey analysis result — data can arrive via csvAnalysisSuccessReport or pdfAnalysisOutput
  isSurveyData = () => {
    const rows = this.getSurveyRows();
    return Array.isArray(rows) && rows.length > 0 && "Sentiment" in rows[0];
  };

  getSurveyRows = () => {
    const { csvAnalysisSuccessReport, pdfAnalysisOutput } = this.props;
    if (csvAnalysisSuccessReport) {
      if (Array.isArray(csvAnalysisSuccessReport?.data)) return csvAnalysisSuccessReport.data;
      if (Array.isArray(csvAnalysisSuccessReport?.report?.data)) return csvAnalysisSuccessReport.report.data;
      if (Array.isArray(csvAnalysisSuccessReport)) return csvAnalysisSuccessReport;
    }
    if (pdfAnalysisOutput) {
      if (Array.isArray(pdfAnalysisOutput?.data)) return pdfAnalysisOutput.data;
      if (Array.isArray(pdfAnalysisOutput)) return pdfAnalysisOutput;
    }
    return [];
  };

  getWordFrequencies = () => {
    const { csvAnalysisSuccessReport } = this.props;
    const wf = csvAnalysisSuccessReport?.word_frequencies
      || csvAnalysisSuccessReport?.report?.word_frequencies;
    return Array.isArray(wf) ? wf : [];
  };

  // Function to render content based on its type
  renderContent = (content, isCSVResult) => {
    if (isCSVResult) {
      return (
        <div
          className="font-inter text-sm leading-relaxed text-gray-700"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    if (this.isHtmlContent(content)) {
      return (
        <div
          className="font-inter text-sm leading-relaxed text-gray-700"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else if (this.isMarkdownContent(content)) {
      return (
        <div className="font-inter text-sm leading-relaxed max-w-none text-gray-700">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => (
                <h1
                  className="text-xl font-bold mb-4 mt-2 text-gray-900 border-b border-blue-100 pb-2"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2
                  className="text-lg font-semibold mb-3 mt-5 text-gray-800 border-b border-gray-100 pb-1.5"
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h3
                  className="text-base font-semibold mb-2 mt-4 text-gray-700"
                  {...props}
                />
              ),
              h4: ({ node, ...props }) => (
                <h4
                  className="text-sm font-semibold mb-1.5 mt-3 text-gray-600"
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p className="text-sm mb-3 text-gray-700 leading-relaxed" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="font-semibold text-gray-900" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="ml-4 mb-3 space-y-1.5" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal ml-5 mb-3 space-y-1.5 text-sm" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="text-sm text-gray-700 leading-relaxed" {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote
                  className="border-l-3 border-blue-200 pl-3 italic mb-3 text-gray-500 text-sm bg-blue-50/30 py-1.5 rounded-r"
                  {...props}
                />
              ),
              code: ({ node, inline, ...props }) =>
                inline ? (
                  <code
                    className="bg-blue-50 px-1.5 py-0.5 rounded text-xs font-mono text-blue-700"
                    {...props}
                  />
                ) : (
                  <code
                    className="block bg-gray-50 border border-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto text-gray-700"
                    {...props}
                  />
                ),
              pre: ({ node, ...props }) => (
                <pre
                  className="bg-gray-50 border border-gray-100 p-3 rounded-lg overflow-x-auto mb-3 text-xs"
                  {...props}
                />
              ),
              a: ({ node, ...props }) => (
                <a
                  className="text-blue-600 underline hover:text-blue-800 hover:no-underline transition-colors"
                  {...props}
                />
              ),
              hr: ({ node, ...props }) => (
                <hr className="my-5 border-gray-100" {...props} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      );
    } else {
      return (
        <pre className="font-inter text-sm leading-relaxed whitespace-pre-wrap text-gray-700">
          {content}
        </pre>
      );
    }
  };

  // Download report as TXT (with HTML tags removed)
  handleDownloadAsTXT = () => {
    const { analysisFileName, location } = this.props;
    let analysisContent = this.handlePathText();

    // Remove HTML tags
    analysisContent = this.stripHtmlTags(analysisContent);

    const element = document.createElement("a");
    const file = new Blob([analysisContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);

    const defaultFileName =
      location.pathname === "/dashboard/result-summary"
        ? "CSV_Analysis_Report"
        : "PDF_Analysis_Report";

    element.download = `${analysisFileName || defaultFileName}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Download report as PDF (with HTML tags removed)
  handleDownloadAsPDF = () => {
    const { analysisFileName, location } = this.props;
    let analysisContent = this.handlePathText();

    // Remove HTML tags
    analysisContent = this.stripHtmlTags(analysisContent);

    const doc = new jsPDF();
    const margin = 10;
    const pageHeight = doc.internal.pageSize.getHeight();
    const lineHeight = 8;
    const maxLineWidth = doc.internal.pageSize.getWidth() - margin * 2;

    const lines = doc.splitTextToSize(analysisContent, maxLineWidth);
    let cursorY = margin;

    lines.forEach((line) => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });

    const defaultFileName =
      location.pathname === "/dashboard/result-summary"
        ? "CSV_Analysis_Report"
        : "PDF_Analysis_Report";

    doc.save(`${analysisFileName || defaultFileName}.pdf`);
  };

  handleDownloadSurveyPDF = async () => {
    const { analysisFileName } = this.props;
    const element = this.contentRef.current;
    if (!element) return;

    this.setState({ isExporting: true });
    try {
      // Scroll to top so html2canvas captures from the correct document position
      window.scrollTo(0, 0);
      await new Promise((r) => setTimeout(r, 150));

      const canvas = await html2canvas(element, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        width: element.offsetWidth,
        height: element.scrollHeight,
        onclone: (clonedDoc) => {
          clonedDoc.querySelectorAll("[data-pdf-exclude]").forEach((el) => {
            el.style.display = "none";
          });
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.8);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const baseName = analysisFileName
        ? analysisFileName.replace(/\.[^/.]+$/, "")
        : "survey_analysis";
      pdf.save(`${baseName}_report.pdf`);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  parseReportSections(text) {
    if (!text) return [];
    const parts = text.split(/(?=^## )/m);
    return parts
      .map((chunk) => {
        const lines = chunk.trim().split("\n");
        const heading = lines[0].replace(/^##\s*/, "").trim();
        const body = lines.slice(1).join("\n").trim();
        return { heading, body };
      })
      .filter((s) => s.heading && s.body);
  }

  extractKeyTakeaways(text, count = 4) {
    if (!text) return [];
    const bullets = [];
    const lines = text.split("\n");
    for (const line of lines) {
      const m = line.match(/^[-*•]\s+(.+)/);
      if (m) {
        bullets.push(m[1].trim());
        if (bullets.length >= count) break;
      }
    }
    return bullets;
  }

  getSectionStyle(heading) {
    const h = heading.toLowerCase();
    if (h.includes("recommend") || h.includes("action") || h.includes("suggest")) {
      return { Icon: Zap, color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", label: "amber" };
    }
    if (h.includes("positive") || h.includes("strength") || h.includes("satisf")) {
      return { Icon: TrendingUp, color: "#10b981", bg: "#ecfdf5", border: "#a7f3d0", label: "green" };
    }
    if (h.includes("negative") || h.includes("concern") || h.includes("issue") || h.includes("challenge") || h.includes("dissatisf")) {
      return { Icon: TrendingDown, color: "#ef4444", bg: "#fef2f2", border: "#fecaca", label: "red" };
    }
    if (h.includes("theme") || h.includes("topic") || h.includes("cluster")) {
      return { Icon: Tag, color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe", label: "indigo" };
    }
    if (h.includes("recur") || h.includes("pattern") || h.includes("feedback")) {
      return { Icon: MessageSquare, color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", label: "blue" };
    }
    return { Icon: Lightbulb, color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe", label: "purple" };
  }

  stripMarkdown(text) {
    return text
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .trim();
  }

  renderSummaryStrip(takeaways) {
    if (!takeaways.length) return null;
    return (
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Key Takeaways</span>
          <div className="flex-1 h-px bg-gray-100" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {takeaways.map((t, i) => (
            <div key={i} className="flex items-start gap-3 bg-white rounded-lg border border-gray-100 px-4 py-3 shadow-sm">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600 mt-0.5">{i + 1}</span>
              <p className="text-sm text-gray-700 leading-relaxed font-inter">{this.stripMarkdown(t)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderSectionCard(section, index) {
    const { Icon, color, bg, border } = this.getSectionStyle(section.heading);
    return (
      <div
        key={index}
        className="rounded-xl overflow-hidden shadow-sm"
        style={{ border: `1px solid ${border}`, background: bg }}
      >
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${border}` }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + "22" }}>
            <Icon style={{ color }} className="w-4 h-4" />
          </div>
          <span className="text-sm font-bold uppercase tracking-wide" style={{ color }}>{section.heading}</span>
        </div>
        <div className="px-5 py-4 bg-white/70">
          <ReactMarkdown components={this.surveyMarkdownComponents}>{section.body}</ReactMarkdown>
        </div>
      </div>
    );
  }

  render() {
    const {
      csvAnalysisSuccess,
      csvAnalysisSuccessReport,
      pdfAnalysisMessage,
      pdfAnalysisOutput,
      analysisFileName,
      location,
      isIndividualFileFetched,
    } = this.props;
    const analysisContent = this.handlePathText();

    // Use includes to allow for dynamic IDs in the URLs
    const isCSVResult = location.pathname.includes("/dashboard/result-summary");
    const isPDFResult = location.pathname.includes(
      "/dashboard/pdf-analyzer/result-summary"
    );

    const isSurvey = this.isSurveyData();
    const surveyRows = isSurvey ? this.getSurveyRows() : [];
    const { showAnalytics, isExporting } = this.state;

    // Show loading while data is being fetched
    const isLoading = isCSVResult && !isIndividualFileFetched && !csvAnalysisSuccessReport;
    if (isLoading) {
      return (
        <div className="relative flex w-full border-l items-center justify-center" style={{ minHeight: '60vh', background: "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff" }}>
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-12 h-12">
              <div className="absolute w-full h-full border-4 border-blue-100 rounded-full"></div>
              <div className="absolute w-full h-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-500 font-inter text-sm">Loading analysis results...</p>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={this.topRef}
        className="relative flex w-full border-l"
        style={{
          background:
            "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
        }}
      >
        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">

          {/* Header row */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="font-SFPro font-bold text-2xl text-gray-900">
                {isSurvey ? "Survey Analysis Results" : "Data Analysis Summary"}
              </h1>
              {analysisFileName && (
                <p className="text-sm text-gray-500 font-inter">{analysisFileName}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isSurvey && (
                <button
                  onClick={this.handleDownloadSurveyPDF}
                  disabled={isExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold hover:from-blue-600 hover:to-blue-700 disabled:opacity-60 transition-all duration-150 font-inter shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  {isExporting ? "Exporting…" : "Download PDF"}
                </button>
              )}
              <button
                onClick={this.handleBackToHome}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-sm font-medium text-gray-600 hover:from-gray-100 hover:to-gray-200 hover:text-gray-900 transition-all duration-150 font-inter"
              >
                <img src={BackbuttonSVG} alt="back" className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* PDF compare button — only for non-survey PDF results */}
          {this.handlePath() && !isSurvey && (
            <div className="flex justify-end">
              <button
                onClick={this.handleCompare}
                className="flex justify-center items-center text-white rounded-lg px-5 h-10 bg-primary-blue font-inter font-semibold text-sm hover:bg-blue-600 transition-all"
              >
                Show Comparison
              </button>
            </div>
          )}

          {/* Content area */}
          <div ref={this.contentRef} className="w-full flex flex-col gap-6">
            {isSurvey ? (
              showAnalytics ? (
                /* ── Analytics view ── */
                <>
                  <button
                    onClick={() => { this.setState({ showAnalytics: false }); this.scrollToTop(); }}
                    className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all font-inter shadow-sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Report
                  </button>
                  <SurveyAnalysisSummary data={surveyRows} fileName={analysisFileName} wordFrequencies={this.getWordFrequencies()} />
                </>
              ) : (
                /* ── Report view (default) ── */
                (() => {
                  const reportText = csvAnalysisSuccessReport?.report_text || "";
                  const totalResponses = csvAnalysisSuccessReport?.total_records || surveyRows.length;
                  const numClusters = csvAnalysisSuccessReport?.num_clusters || 0;
                  const questionCount = surveyRows.length > 0
                    ? Object.keys(surveyRows[0]).filter(k => !DataAnalysisSummary.ANALYSIS_KEYS.has(k)).length
                    : 0;

                  const respondentRegex = /Respondent\s+(\d+)\s+participated([\s\S]*?)(?=Respondent\s+\d+\s+participated|Global Analysis|$)/gi;
                  const respondents = [];
                  let m;
                  while ((m = respondentRegex.exec(reportText)) !== null) {
                    respondents.push({ num: m[1], body: ("Respondent " + m[1] + " participated" + m[2]).trim() });
                  }
                  const globalMatch = reportText.match(/Global Analysis[\s\S]*?(?:\n)([\s\S]*)$/i);
                  const globalText = globalMatch ? globalMatch[1].trim() : "";

                  return (
                    <div className="flex flex-col gap-6">
                      {/* Survey Report header card */}
                      <div className="rounded-xl border border-blue-100 bg-white shadow-sm overflow-hidden">
                        <div className="px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex items-center justify-between">
                          <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest font-inter">Survey Report</span>
                        </div>
                        <div className="px-6 py-5 flex gap-6">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                              <Users className="w-4 h-4 text-blue-500" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-blue-600 font-inter">{totalResponses.toLocaleString()}</p>
                              <p className="text-xs text-gray-400">Total Responses</p>
                            </div>
                          </div>
                          {questionCount > 0 && (
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                                <BarChart2 className="w-4 h-4 text-indigo-500" />
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-indigo-600 font-inter">{questionCount}</p>
                                <p className="text-xs text-gray-400">Questions</p>
                              </div>
                            </div>
                          )}
                          {numClusters > 0 && (
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                                <Layers className="w-4 h-4 text-purple-500" />
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-purple-600 font-inter">{numClusters}</p>
                                <p className="text-xs text-gray-400">Themes</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Global Analysis — section cards */}
                      {globalText ? (() => {
                        const sections = this.parseReportSections(globalText);
                        const takeaways = this.extractKeyTakeaways(globalText, 4);
                        return (
                          <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Global Analysis</span>
                              <div className="flex-1 h-px bg-gray-100" />
                            </div>
                            {this.renderSummaryStrip(takeaways)}
                            {sections.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {sections.map((s, i) => this.renderSectionCard(s, i))}
                              </div>
                            ) : (
                              <div className="rounded-xl border border-indigo-100 bg-white px-6 py-5 shadow-sm">
                                <ReactMarkdown components={this.surveyMarkdownComponents}>{globalText}</ReactMarkdown>
                              </div>
                            )}
                          </div>
                        );
                      })() : reportText === "" && (
                        <div className="rounded-xl border border-gray-100 bg-gray-50 px-6 py-8 text-center">
                          <p className="text-sm text-gray-400">No AI narrative report available for this survey.</p>
                        </div>
                      )}

                      {/* Individual Respondent Analysis — below */}
                      {respondents.length > 0 && (
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest font-inter">
                              Individual Respondent Analysis
                            </span>
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                              {respondents.length} sampled
                            </span>
                          </div>
                          {respondents.map((r) => (
                            <div key={r.num} className="flex gap-3 bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold font-inter mt-0.5">
                                {r.num}
                              </div>
                              <div className="flex-1">
                                <ReactMarkdown components={this.surveyMarkdownComponents}>{r.body}</ReactMarkdown>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* View Detailed Analysis button */}
                      <div className="flex justify-center pb-4">
                        <button
                          onClick={() => { this.setState({ showAnalytics: true }); this.scrollToTop(); }}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-primary-blue to-blue-500 text-white text-sm font-semibold hover:from-blue-600 hover:to-blue-600 transition-all font-inter shadow-sm"
                        >
                          View Detailed Analysis
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()
              )
            ) : (
              <div
                className="w-full rounded-xl border border-blue-100 bg-white px-6 md:px-10 py-8 shadow-sm report-container overflow-auto"
                style={{ maxHeight: "75vh", background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)" }}
              >
                {(csvAnalysisSuccess ||
                  pdfAnalysisMessage ||
                  pdfAnalysisOutput ||
                  isIndividualFileFetched) &&
                analysisContent ? (
                  this.renderContent(analysisContent, isCSVResult)
                ) : (
                  <div className="text-gray-400 text-sm font-inter">No data available</div>
                )}
              </div>
            )}
          </div>

          {/* Download buttons — only for non-survey */}
          {!isSurvey && (
            <div className="flex justify-end gap-3 pb-6">
              <button
                onClick={this.handleDownloadAsTXT}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 font-inter shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Download TXT
              </button>
              <button
                onClick={this.handleDownloadAsPDF}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-150 font-inter shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Download PDF
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  csvAnalysisSuccessReport: state.analysis.csvAnalysisSuccessReport,
  csvAnalysisSuccess: state.analysis.csvAnalysisSuccess,
  analysisFileName: state.analysis.analysisFileName,
  pdfAnalysisMessage: state.analysis.pdfAnalysisMessage,
  pdfAnalysisOutput: state.analysis.pdfAnalysisOutput,
  AddedFileID: state.analysis.AddedFileID,
  isIndividualFileFetched: state.analysis.isIndividualFileFetched,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    ResetAnalysisReduxStateAction,
    ResetAnalysisMessageAction,
    FetchIndividualFileAction,
  })(DataAnalysisSummary)
);
